import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'
import {ThemeModeComponent} from '../../../assets/ts/layout'

export type ThemeModeType = 'dark' | 'light' | 'system'
export type LayoutWidthType = 'full' | 'fixed'
const getSystemMode = () => ThemeModeComponent.getSystemMode() as 'light' | 'dark'

type ThemeModeContextType = {
  mode: ThemeModeType
  menuMode: ThemeModeType
  layoutWidth: LayoutWidthType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
  updateLayoutWidth: (_layoutWidth: LayoutWidthType) => void
}

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  const mode = _mode !== 'system' ? _mode : getSystemMode()
  document.body.style.backgroundImage = 'none'
  document.documentElement.style.colorScheme = mode
}

const themeModeLSKey = 'kt_theme_mode_value'
const themeMenuModeLSKey = 'kt_theme_mode_menu'
const layoutWidthLSKey = 'kt_layout_width_mode'

const getThemeModeFromLocalStorage = (lsKey: string): ThemeModeType => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'light'
  }

  const data = window.localStorage.getItem(lsKey)
  if (!data) {
    return 'light'
  }

  if (data === 'light') {
    return 'light'
  }

  if (data === 'dark') {
    return 'dark'
  }

  return 'system'
}

const getLayoutWidthFromLocalStorage = (lsKey: string): LayoutWidthType => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'full'
  }

  const data = window.localStorage.getItem(lsKey)

  if (data === 'fixed') {
    return 'fixed'
  }

  return 'full'
}

const hasStoredMenuMode =
  typeof window !== 'undefined' && Boolean(window.localStorage?.getItem(themeMenuModeLSKey))
const defaultMenuMode = hasStoredMenuMode
  ? getThemeModeFromLocalStorage(themeMenuModeLSKey)
  : 'system'
const storedMode = getThemeModeFromLocalStorage(themeModeLSKey)
const defaultMode =
  defaultMenuMode === 'system'
    ? getSystemMode()
    : storedMode === 'system'
    ? getSystemMode()
    : storedMode
const defaultLayoutWidth = getLayoutWidthFromLocalStorage(layoutWidthLSKey)

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultMode,
  menuMode: defaultMenuMode,
  layoutWidth: defaultLayoutWidth,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
  updateLayoutWidth: (_layoutWidth: LayoutWidthType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setMode] = useState<ThemeModeType>(defaultMode)
  const [menuMode, setMenuMode] = useState<ThemeModeType>(defaultMenuMode)
  const [layoutWidth, setLayoutWidth] = useState<LayoutWidthType>(defaultLayoutWidth)

  const updateMode = useCallback((_mode: ThemeModeType) => {
    const updatedMode = _mode === 'system' ? getSystemMode() : _mode
    setMode(updatedMode)
    themeModeSwitchHelper(updatedMode)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(themeModeLSKey, updatedMode)
    }
    document.documentElement.setAttribute('data-theme', updatedMode)
    ThemeModeComponent.flipImages()
  }, [])

  const updateMenuMode = useCallback((_menuMode: ThemeModeType) => {
    setMenuMode(_menuMode)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(themeMenuModeLSKey, _menuMode)
    }
  }, [])

  const updateLayoutWidth = (_layoutWidth: LayoutWidthType) => {
    setLayoutWidth(_layoutWidth)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(layoutWidthLSKey, _layoutWidth)
    }
  }

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const {body} = document
    body.classList.remove('layout-full-width', 'layout-fixed-width')
    body.classList.add(layoutWidth === 'full' ? 'layout-full-width' : 'layout-fixed-width')

    if (layoutWidth !== 'full') {
      body.classList.remove('aside-hidden')
    }
  }, [layoutWidth])

  useEffect(() => {
    updateMode(menuMode)
    updateMenuMode(menuMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (menuMode !== 'system' || typeof window.matchMedia !== 'function') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => updateMode('system')
    mediaQuery.addEventListener?.('change', handleSystemThemeChange)

    return () => mediaQuery.removeEventListener?.('change', handleSystemThemeChange)
  }, [menuMode, updateMode])

  return (
    <ThemeModeContext.Provider
      value={{mode, menuMode, layoutWidth, updateMode, updateMenuMode, updateLayoutWidth}}
    >
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, useThemeMode, getSystemMode, themeModeSwitchHelper}
